import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import "../style/Studentdatapage.css";
import "../style/approvalpage.css";
import { PageHeader, SearchInput } from "../components/admin/AdminUI";
import axios from "axios";
import Swal from "sweetalert2";
import SyncLoader from "react-spinners/SyncLoader";
import PaymentAllocationSimulator from "./paymentSimulator/PaymentAllocationSimulator";
import { formatDmy } from "../utils/dateFormat";

const CLASS_ORDER = ["OT", "NT", "BD", "BLC", "BCMF", "MASTERCLASS", "HH", "CBH", "CBD", "BWG", "MMM", "CONFERENCE"];

// Semua diskon (CGL/Coach, scholarship, CEA To Church) diputuskan manual oleh
// admin saat approve — dropdown ini dipakai konsisten di semua alur approve.
const discountOptions = { 0: "0%", 25: "25%", 50: "50%", 75: "75%", 100: "100%" };

const formatRupiah = (n) =>
  typeof n === "number" ? `Rp${n.toLocaleString("id-ID")}` : "-";

// Label & warna status di kolom Status. "unpaid" = belum di-approve admin.
const STATUS_META = {
  unpaid: { label: "Waiting Approval", cls: "ap-status--waiting" },
  awaiting_payment: { label: "Awaiting Payment", cls: "ap-status--awaiting" },
  payment_failed: { label: "Payment Failed", cls: "ap-status--failed" },
  paid_fulfillment_error: { label: "Needs Review", cls: "ap-status--failed" },
};
const statusMeta = (status) => STATUS_META[status] || STATUS_META.unpaid;

// Tab filter di atas tabel. Jumlah di tiap tab dihitung dari hasil pencarian.
const STATUS_TABS = [
  { key: "all", label: "All", match: () => true },
  { key: "waiting", label: "Waiting Approval", match: (r) => (r.paymentStatus || "unpaid") === "unpaid" },
  { key: "awaiting", label: "Awaiting Payment", match: (r) => r.paymentStatus === "awaiting_payment" },
  {
    key: "failed",
    label: "Payment Failed",
    match: (r) => r.paymentStatus === "payment_failed" || r.paymentStatus === "paid_fulfillment_error",
  },
  { key: "early", label: "Early Regis", match: (r) => !!r.earlyRegistration },
];

// Ringkas kelas "requested" per batch, mis. "Batch 6 · OT → CONFERENCE".
// Maksimal 3 kelas ditulis lengkap; lebih dari itu ditampilkan sebagai rentang.
const summarizeRequestedClasses = (classList) => {
  try {
    const cl = typeof classList === "string" ? JSON.parse(classList) : classList;
    const requested = (cl?.className || []).filter((c) => c.status === "requested");
    const groups = {};
    requested.forEach((c) => {
      const key = c.batch || "";
      if (!groups[key]) groups[key] = [];
      groups[key].push(c.name);
    });
    return Object.keys(groups)
      .sort((a, b) => Number(a) - Number(b))
      .map((batch) => {
        const names = groups[batch].sort((a, b) => CLASS_ORDER.indexOf(a) - CLASS_ORDER.indexOf(b));
        const text = names.length <= 3 ? names.join(", ") : `${names[0]} → ${names[names.length - 1]}`;
        return { batch, names, text };
      });
  } catch {
    return [];
  }
};

const ApprovalPage = ({ setLocationAdmin }) => {
  const address = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [studentData, setStudentData] = useState([]);
  const [churchRegs, setChurchRegs] = useState([]);
  const [churchDetails, setChurchDetails] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [errorMe, setErrorMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const [statusTab, setStatusTab] = useState("all");
  const [allocationReg, setAllocationReg] = useState(null);
  const [allocSubmitting, setAllocSubmitting] = useState(false);
  const [churchAllocations, setChurchAllocations] = useState([]);
  const [allocationsLoading, setAllocationsLoading] = useState(false);
  const [churchEmailBusy, setChurchEmailBusy] = useState(false);
  const [resendingAllocId, setResendingAllocId] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(null);

  const getMe = async () => {
    try {
      await axios.get("/meAdmin");
    } catch (error) {
      setErrorMe(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setLocationAdmin(address);
    getMe();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [approvalRes, churchRes] = await Promise.all([
        axios.get("/approval"),
        axios.get("/churchregistrations").catch(() => ({ data: [] })),
      ]);
      setStudentData(approvalRes.data);
      setChurchRegs(churchRes.data);
    } catch (error) {
      setErrorMe(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const normalizedSearch = search.toLowerCase().replace(/\s+/g, "");

      const churchRows = churchRegs
        .filter((reg) => {
          const nameMatched = reg.churchName.toLowerCase().replace(/\s+/g, "").includes(normalizedSearch);
          const picMatched = reg.picName.toLowerCase().replace(/\s+/g, "").includes(normalizedSearch);
          const emailMatched = reg.picEmail.toLowerCase().replace(/\s+/g, "").includes(normalizedSearch);
          return nameMatched || picMatched || emailMatched;
        })
        .map((reg) => ({ ...reg, _type: "church" }));

      const regularRows = studentData
        .filter((item) => {
          const namematched = item.name.toLowerCase().replace(/\s+/g, "").includes(normalizedSearch);
          const emailmatched = item.email.toLowerCase().replace(/\s+/g, "").includes(normalizedSearch);
          const studentIdmatched = item.studentId.toLowerCase().replace(/\s+/g, "").includes(normalizedSearch);
          return namematched || emailmatched || studentIdmatched;
        })
        .sort((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "base" }))
        .map((item) => ({ ...item, _type: "regular" }));

      setFiltered([...churchRows, ...regularRows]);
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [studentData, churchRegs, search]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleSeeDetailsStudent = async (email) => {
    try {
      const res = await axios.get(`/approval/${email}`);
      setStudentDetails(res.data);
    } catch (error) {
      await Swal.fire({
        text: error.response?.data?.msg || "Something went wrong",
        icon: "error",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
      });
    }
  };

  // paymentStatus "payment_failed" → tombolnya jadi "Resend Payment Link":
  // endpoint-nya sama (POST /student), hanya teks dialognya yang berbeda.
  const handleApprove = async (id, currentDiscount = 0, paymentStatus) => {
    const isResend = paymentStatus === "payment_failed";
    const { value: discountPercent, isConfirmed } = await Swal.fire({
      title: isResend ? "Resend Payment Link" : "Approval Confirmation",
      text: isResend
        ? "The previous payment failed or expired. Send a new payment link to the student's email? You can adjust the discount below."
        : "Approve this registration? If a payment is required, a payment link will be sent to the student's email — they only become an official student once payment is confirmed.",
      icon: "question",
      input: "select",
      inputOptions: discountOptions,
      inputValue: String(currentDiscount || 0),
      background: "white",
      color: "black",
      backdrop: `rgba(131, 131, 131, 0.5)`,
      showCancelButton: true,
      confirmButtonColor: "#0004ff",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
      customClass: {
        confirmButton: "custom-btn-sweetalrt",
      },
    });
    if (!isConfirmed) return;

    try {
      const payload = {
        email: id,
        discountPercent: Number(discountPercent),
      };
      const res = await axios.post(`/student`, payload);

      const message = res.data.paymentRequired
        ? `${isResend ? "New payment" : "Payment"} link sent to the student's email! Amount due: ${formatRupiah(res.data.price?.finalPrice)}`
        : "Data Approved! No payment required.";

      await Swal.fire({
        text: message,
        icon: "success",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        showCancelButton: false,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "custom-btn-sweetalrt",
        },
      });
      fetchData();
    } catch (error) {
      await Swal.fire({
        text: error.response?.data?.msg || "Something went wrong",
        icon: "error",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
      });
    }
  };

  // Kirim ulang link untuk pendaftar yang masih "awaiting_payment". Backend
  // (POST /approval/:id/resend) membatalkan transaksi lama di Midtrans dan
  // mengganti order-id, jadi link /pay/<order-lama> di email sebelumnya langsung
  // tidak berlaku. Nominal & diskon sama dengan link pertama.
  const handleResendAwaiting = async (item) => {
    const { isConfirmed } = await Swal.fire({
      title: "Resend Payment Link",
      text: `Send a new payment link to ${item.email}? The previous payment link will stop working.`,
      icon: "question",
      background: "white",
      color: "black",
      backdrop: `rgba(131, 131, 131, 0.5)`,
      showCancelButton: true,
      confirmButtonColor: "#0004ff",
      cancelButtonColor: "#d33",
      confirmButtonText: "Resend",
      customClass: { confirmButton: "custom-btn-sweetalrt" },
    });
    if (!isConfirmed) return;

    setResendingEmail(item.email);
    try {
      const res = await axios.post(`/approval/${encodeURIComponent(item.email)}/resend`);
      await Swal.fire({
        text: res.data.msg,
        icon: "success",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
        customClass: { confirmButton: "custom-btn-sweetalrt" },
      });
    } catch (error) {
      await Swal.fire({
        text: error.response?.data?.msg || "Failed to resend the payment link.",
        icon: "error",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
      });
    } finally {
      setResendingEmail(null);
      fetchData();
    }
  };

  // paymentStatus "awaiting_payment" → tombolnya jadi "Cancel Registration":
  // backend membatalkan link pembayaran yang masih aktif lalu menghapus datanya.
  const handleReject = async (id, paymentStatus) => {
    const isCancel = paymentStatus === "awaiting_payment";
    const result = await Swal.fire({
      title: isCancel ? "Cancel Registration" : "Reject Confirmation",
      text: isCancel
        ? "This student has an unpaid payment link. Cancel the registration? The payment link will be voided and the registration data will be removed."
        : "Are you sure want to reject this data ?",
      icon: "warning",
      background: "white",
      color: "black",
      backdrop: `rgba(131, 131, 131, 0.5)`,
      showCancelButton: true,
      confirmButtonColor: "#0004ff",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
      customClass: {
        confirmButton: "custom-btn-sweetalrt",
      },
    });
    if (result.isConfirmed) {
      try {
        await axios.delete(`/approval/${id}`);
      } catch (error) {
        // Mis. link sudah terlanjur dibayar sehingga tidak bisa dibatalkan.
        await Swal.fire({
          text: error.response?.data?.msg || "Something went wrong",
          icon: "error",
          background: "white",
          color: "black",
          backdrop: `rgba(131, 131, 131, 0.5)`,
          confirmButtonColor: "#0004ff",
          confirmButtonText: "OK",
        });
        fetchData();
        return;
      }

      await Swal.fire({
        text: isCancel ? "Registration Cancelled!" : "Data Rejected!",
        icon: "success",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        showCancelButton: false,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "custom-btn-sweetalrt",
        },
      });
      fetchData();
    } else if (result.isDenied) {
    }
  };

  // Perbaiki email pendaftar yang salah ketik (mis. tidak menerima link bayar).
  // Kalau statusnya sudah awaiting_payment, backend membatalkan invoice lama &
  // mengirim ulang link ke email yang benar.
  const handleEditEmail = async (email) => {
    const { value: newEmail } = await Swal.fire({
      title: "Edit Registrant Email",
      input: "email",
      inputLabel: "Correct email address",
      inputValue: email,
      inputPlaceholder: "correct@email.com",
      background: "white",
      color: "black",
      backdrop: `rgba(131, 131, 131, 0.5)`,
      showCancelButton: true,
      confirmButtonColor: "#0004ff",
      cancelButtonColor: "#d33",
      confirmButtonText: "Save",
      customClass: { confirmButton: "custom-btn-sweetalrt" },
      inputValidator: (v) => (!v ? "Please enter an email address" : undefined),
    });
    if (!newEmail || newEmail.trim().toLowerCase() === email.toLowerCase()) return;
    try {
      const res = await axios.patch(`/approval/${email}/email`, { newEmail: newEmail.trim() });
      await Swal.fire({
        text: res.data.msg,
        icon: "success",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
        customClass: { confirmButton: "custom-btn-sweetalrt" },
      });
      fetchData();
    } catch (err) {
      await Swal.fire({
        text: err.response?.data?.msg || "Failed to update email.",
        icon: "error",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
        customClass: { confirmButton: "custom-btn-sweetalrt" },
      });
    }
  };

  // Buka detail CEA to Church + ambil status pembayaran tiap payer.
  const openChurchDetails = async (item) => {
    setChurchDetails(item);
    setChurchAllocations([]);
    setAllocationsLoading(true);
    try {
      const res = await axios.get(`/churchregistration/${item.id}/allocations`);
      setChurchAllocations(res.data);
    } catch {
      setChurchAllocations([]);
    } finally {
      setAllocationsLoading(false);
    }
  };

  // Buka overlay alokasi (pilih diskon + recipients). Approve nyata dilakukan
  // saat admin confirm di dalam overlay (handleAllocationConfirm).
  const handleApproveChurch = (reg) => {
    setChurchDetails(null);
    const students = typeof reg.students === "string" ? JSON.parse(reg.students) : reg.students;
    setAllocationReg({ ...reg, students });
  };

  const handleAllocationConfirm = async ({ discountPercent, recipients }) => {
    if (!allocationReg) return;
    setAllocSubmitting(true);
    try {
      const res = await axios.post(`/churchregistration/${allocationReg.id}/approve-allocation`, {
        discountPercent,
        recipients,
      });
      setAllocationReg(null);
      await Swal.fire({
        text: res.data.paymentRequired
          ? "Payment links have been sent to each payer's email. Each student becomes official once their share is paid."
          : "All students approved! No payment required.",
        icon: "success",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
        customClass: { confirmButton: "custom-btn-sweetalrt" },
      });
      fetchData();
    } catch (error) {
      await Swal.fire({
        text: error.response?.data?.msg || "Something went wrong",
        icon: "error",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
      });
    } finally {
      setAllocSubmitting(false);
    }
  };

  const handleRejectChurch = async (reg) => {
    setChurchDetails(null);
    const isCancel = reg.paymentStatus === "awaiting_payment";
    const result = await Swal.fire({
      title: isCancel ? "Cancel Registration" : "Reject Confirmation",
      text: isCancel
        ? `${reg.churchName} has an unpaid payment link. Cancel this registration? The payment link will be voided and the registration data will be removed.`
        : `Reject the registration from ${reg.churchName}?`,
      icon: "warning",
      background: "white",
      color: "black",
      backdrop: `rgba(131, 131, 131, 0.5)`,
      showCancelButton: true,
      confirmButtonColor: "#0004ff",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes",
      customClass: {
        confirmButton: "custom-btn-sweetalrt",
      },
    });
    if (!result.isConfirmed) return;

    try {
      await axios.delete(`/churchregistration/${reg.id}`);
      await Swal.fire({
        text: isCancel ? "Registration Cancelled!" : "Registration Rejected!",
        icon: "success",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
        customClass: {
          confirmButton: "custom-btn-sweetalrt",
        },
      });
    } catch (error) {
      await Swal.fire({
        text: error.response?.data?.msg || "Something went wrong",
        icon: "error",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
      });
    }
    fetchData();
  };

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account !",
        icon: "warning",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        showCancelButton: false,
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
        customClass: {
          confirmButton: "custom-btn-sweetalrt",
        },
      });

      navigate("/loginAdmin");
    }
  }, [errorMe]);

  const detailRow = (label, value) => {
    if (!value) return null;
    return (
      <div style={{ display: "flex", padding: "3px 0", fontSize: "12.5px" }}>
        <span style={{ width: "150px", minWidth: "150px", color: "#888" }}>{label}</span>
        <span style={{ color: "#222", fontWeight: 500, wordBreak: "break-word" }}>{value}</span>
      </div>
    );
  };

  // Sama seperti detailRow, tapi dengan tombol Edit di sebelah nilainya —
  // dipakai untuk email PIC & email tiap student pada pendaftaran CEA To Church.
  const editableEmailRow = (label, value, onEdit) => (
    <div style={{ display: "flex", padding: "3px 0", fontSize: "12.5px", alignItems: "baseline" }}>
      <span style={{ width: "150px", minWidth: "150px", color: "#888" }}>{label}</span>
      <span style={{ color: "#222", fontWeight: 500, wordBreak: "break-word" }}>
        {value || <span style={{ color: "#bbb", fontWeight: 400 }}>—</span>}
      </span>
      <button
        type="button"
        onClick={onEdit}
        disabled={churchEmailBusy}
        style={{
          marginLeft: "8px",
          background: "none",
          border: "none",
          padding: 0,
          color: churchEmailBusy ? "#bbb" : "#0004ff",
          fontSize: "11.5px",
          fontWeight: 700,
          cursor: churchEmailBusy ? "default" : "pointer",
          textDecoration: "underline",
          whiteSpace: "nowrap",
        }}
      >
        Edit
      </button>
    </div>
  );

  // Ambil ulang data registrasi + alokasinya supaya isi modal langsung
  // menampilkan email yang baru (tanpa admin perlu menutup & membuka lagi).
  const refreshChurchDetails = async (id) => {
    try {
      const [regsRes, allocRes] = await Promise.all([
        axios.get("/churchregistrations"),
        axios.get(`/churchregistration/${id}/allocations`).catch(() => ({ data: [] })),
      ]);
      setChurchRegs(regsRes.data);
      const updated = (regsRes.data || []).find((r) => r.id === id);
      if (updated) setChurchDetails(updated);
      setChurchAllocations(allocRes.data);
    } catch {}
  };

  // Kirim link pembayaran baru untuk SATU payer CEA To Church yang statusnya
  // gagal/kedaluwarsa. Penyapu otomatis tidak lagi mengirim email, jadi admin
  // yang menekan tombol ini secara manual.
  const handleResendAllocation = async (alloc) => {
    const { isConfirmed } = await Swal.fire({
      title: "Resend Payment Link",
      text: `Send a new payment link of ${formatRupiah(alloc.amount)} to ${alloc.payerEmail}?`,
      icon: "question",
      background: "white",
      color: "black",
      backdrop: `rgba(131, 131, 131, 0.5)`,
      showCancelButton: true,
      confirmButtonColor: "#0004ff",
      cancelButtonColor: "#d33",
      confirmButtonText: "Resend",
      customClass: { confirmButton: "custom-btn-sweetalrt" },
    });
    if (!isConfirmed) return;

    setResendingAllocId(alloc.id);
    try {
      const res = await axios.post(`/church-allocation/${alloc.id}/resend`);
      const emailFailed = (res.data.emailFailures || []).length > 0;
      await Swal.fire({
        text: res.data.msg,
        icon: emailFailed ? "warning" : "success",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
        customClass: { confirmButton: "custom-btn-sweetalrt" },
      });
    } catch (error) {
      await Swal.fire({
        text: error.response?.data?.msg || "Failed to resend the payment link.",
        icon: "error",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
        customClass: { confirmButton: "custom-btn-sweetalrt" },
      });
    } finally {
      setResendingAllocId(null);
      if (churchDetails) refreshChurchDetails(churchDetails.id);
    }
  };

  // Perbaiki email PIC / student yang salah ketik pada pendaftaran CEA To Church.
  // Kalau link pembayarannya sudah terlanjur terkirim ke email yang salah,
  // backend membatalkan invoice lama & mengirim ulang ke email yang benar.
  const handleChurchEmailEdit = async (scope, currentEmail, personName) => {
    const { value: newEmail } = await Swal.fire({
      title: scope === "pic" ? "Edit PIC Email" : "Edit Student Email",
      input: "email",
      inputLabel: personName ? `Correct email for ${personName}` : "Correct email address",
      inputValue: currentEmail || "",
      inputPlaceholder: "correct@email.com",
      background: "white",
      color: "black",
      backdrop: `rgba(131, 131, 131, 0.5)`,
      showCancelButton: true,
      confirmButtonColor: "#0004ff",
      cancelButtonColor: "#d33",
      confirmButtonText: "Save",
      customClass: { confirmButton: "custom-btn-sweetalrt" },
      inputValidator: (v) => (!v ? "Please enter an email address" : undefined),
    });
    if (!newEmail || newEmail.trim().toLowerCase() === (currentEmail || "").toLowerCase()) return;

    setChurchEmailBusy(true);
    try {
      const res = await axios.patch(`/churchregistration/${churchDetails.id}/email`, {
        scope,
        oldEmail: currentEmail,
        newEmail: newEmail.trim(),
      });
      await refreshChurchDetails(churchDetails.id);
      await Swal.fire({
        text: res.data.msg,
        icon: "success",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
        customClass: { confirmButton: "custom-btn-sweetalrt" },
      });
    } catch (err) {
      await Swal.fire({
        text: err.response?.data?.msg || "Failed to update email.",
        icon: "error",
        background: "white",
        color: "black",
        backdrop: `rgba(131, 131, 131, 0.5)`,
        confirmButtonColor: "#0004ff",
        confirmButtonText: "OK",
        customClass: { confirmButton: "custom-btn-sweetalrt" },
      });
    } finally {
      setChurchEmailBusy(false);
    }
  };

  if (isLoading) {
    return (
      <SyncLoader
        color={"#fb3104"}
        size={10}
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
      />
    );
  }
  const tabCounts = STATUS_TABS.reduce((acc, tab) => {
    acc[tab.key] = filtered.filter(tab.match).length;
    return acc;
  }, {});
  const activeTab = STATUS_TABS.find((tab) => tab.key === statusTab) || STATUS_TABS[0];
  const visibleRows = filtered.filter(activeTab.match);

  const renderActions = (item, { onDetails, onEditEmail, onApprove, onReject, onResend, resending }) => {
    const status = item.paymentStatus || "unpaid";
    return (
      <div className="ap-actions">
        <div className="ap-actions-icons">
          <button type="button" className="ap-icon-btn" title="See details" aria-label="See details" onClick={onDetails}>
            <i className="bi bi-eye"></i>
          </button>
          {onEditEmail && (
            <button type="button" className="ap-icon-btn" title="Edit email" aria-label="Edit email" onClick={onEditEmail}>
              <i className="bi bi-envelope"></i>
            </button>
          )}
        </div>
        <div className="ap-actions-main">
          {status !== "awaiting_payment" && (
            <button type="button" className="ap-btn ap-btn--primary" onClick={onApprove}>
              {status === "payment_failed" ? "Resend Payment Link" : "Approve"}
            </button>
          )}
          {status === "awaiting_payment" && onResend && (
            <button type="button" className="ap-btn ap-btn--primary" onClick={onResend} disabled={resending}>
              {resending ? "Sending..." : "Resend Payment Link"}
            </button>
          )}
          <button type="button" className="ap-btn ap-btn--danger" onClick={onReject}>
            {status === "awaiting_payment" ? "Cancel Registration" : "Reject"}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="ap-page">
      <div className="adm-main">
        <PageHeader
          title="Approval"
          subtitle="Registrations waiting for admin review and payment."
          actions={<SearchInput value={search} onChange={setSearch} placeholder="Search name, email, or student ID" />}
        />

        <div className="ap-tabs">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`ap-tab${statusTab === tab.key ? " ap-tab--active" : ""}`}
              onClick={() => setStatusTab(tab.key)}
            >
              {tab.label}
              <span className="ap-tab-count">{tabCounts[tab.key]}</span>
            </button>
          ))}
        </div>

        <div className="ap-card">
          {visibleRows.length === 0 ? (
            <div className="ap-empty">
              <i className="bi bi-inbox"></i>
              <p className="ap-empty-title">No registrations here</p>
              <p className="ap-empty-text">
                {search ? "No registrations match your search." : "There is nothing waiting in this category."}
              </p>
            </div>
          ) : (
            <div className="ap-table-wrap">
              <table className="ap-table">
                <thead>
                  <tr>
                    <th>Registrant</th>
                    <th>Program</th>
                    <th>Classes</th>
                    <th>Status</th>
                    <th className="ap-th-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((item) => {
                    const status = statusMeta(item.paymentStatus);

                    if (item._type === "church") {
                      const students =
                        typeof item.students === "string" ? JSON.parse(item.students) : item.students;
                      return (
                        <tr key={`church-${item.id}`}>
                          <td>
                            <div className="ap-name">{item.churchName}</div>
                            <div className="ap-meta">PIC: {item.picName}</div>
                            <div className="ap-badges">
                              <span className="ap-badge ap-badge--church">CEA To Church</span>
                            </div>
                          </td>
                          <td>
                            <div>CEA To Church</div>
                            <div className="ap-meta">
                              {students.length} student{students.length > 1 ? "s" : ""}
                            </div>
                          </td>
                          <td>
                            <span className="ap-muted">See details</span>
                          </td>
                          <td>
                            <span className={`ap-status ${status.cls}`}>{status.label}</span>
                          </td>
                          <td>
                            {renderActions(item, {
                              onDetails: () => openChurchDetails(item),
                              onApprove: () => handleApproveChurch(item),
                              onReject: () => handleRejectChurch(item),
                            })}
                          </td>
                        </tr>
                      );
                    }

                    const classGroups = summarizeRequestedClasses(item.classList);
                    return (
                      <tr key={item.email}>
                        <td>
                          <div className="ap-name">{item.name}</div>
                          <div className="ap-meta">{item.studentId}</div>
                          <div className="ap-badges">
                            {item.earlyRegistration && <span className="ap-badge ap-badge--early">Early Regis</span>}
                            {item.needsReview && <span className="ap-badge ap-badge--guest">Special Guest</span>}
                          </div>
                        </td>
                        <td>
                          <div>{item.program}</div>
                          <div className="ap-meta">{item.role || "-"}</div>
                        </td>
                        <td>
                          {classGroups.length === 0 ? (
                            <span className="ap-muted">-</span>
                          ) : (
                            classGroups.map((group) => (
                              <div key={group.batch || "none"} className="ap-class-line" title={group.names.join(", ")}>
                                {group.batch && <span className="ap-class-batch">Batch {group.batch} · </span>}
                                {group.text}
                                {group.names.length > 3 && (
                                  <span className="ap-class-count"> ({group.names.length} classes)</span>
                                )}
                              </div>
                            ))
                          )}
                        </td>
                        <td>
                          <span className={`ap-status ${status.cls}`}>{status.label}</span>
                        </td>
                        <td>
                          {renderActions(item, {
                            onDetails: () => handleSeeDetailsStudent(item.email),
                            onEditEmail: () => handleEditEmail(item.email),
                            onApprove: () => handleApprove(item.email, item.discountPercent, item.paymentStatus),
                            onReject: () => handleReject(item.email, item.paymentStatus),
                            onResend: () => handleResendAwaiting(item),
                            resending: resendingEmail === item.email,
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ══════════ CEA TO CHURCH — SEE DETAILS MODAL ══════════ */}
      {churchDetails &&
        createPortal(
          <div className="adm-modal-backdrop" onClick={() => setChurchDetails(null)}>
            <div
              className="adm-modal"
              onClick={(e) => e.stopPropagation()}
              style={{ width: "680px", padding: "24px" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "16px",
                }}
              >
                <div>
                  <h5 style={{ margin: 0, fontWeight: 700 }}>{churchDetails.churchName}</h5>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#888" }}>
                    CEA To Church Registration
                  </p>
                </div>
                <button
                  onClick={() => setChurchDetails(null)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "20px",
                    lineHeight: 1,
                    cursor: "pointer",
                    color: "#888",
                  }}
                >
                  ×
                </button>
              </div>

              <div
                style={{
                  backgroundColor: "#fff8f7",
                  border: "1px solid #fbd5cc",
                  borderRadius: "6px",
                  padding: "14px 16px",
                  marginBottom: "18px",
                }}
              >
                <p style={{ margin: "0 0 8px", fontSize: "12px", fontWeight: 700, color: "#fb3104", textTransform: "uppercase" }}>
                  Contact Person
                </p>
                {detailRow("Name", churchDetails.picName)}
                {editableEmailRow("Email", churchDetails.picEmail, () =>
                  handleChurchEmailEdit("pic", churchDetails.picEmail, churchDetails.picName),
                )}
                {detailRow("Whatsapp", churchDetails.picPhone)}
                {detailRow("Church City", churchDetails.churchCity)}
                {detailRow("Payment Plan", churchDetails.paymentPlan === "installment" ? "Installment" : "Full Payment")}
                {detailRow(
                  "Submitted",
                  formatDmy(churchDetails.createdAt),
                )}
              </div>

              {/* ── Payment status per payer (setelah di-approve dgn alokasi) ── */}
              {(allocationsLoading || churchAllocations.length > 0) && (
                <div style={{ border: "1px solid #e5e5e5", borderRadius: "8px", padding: "14px 16px", marginBottom: "18px" }}>
                  <p style={{ margin: "0 0 10px", fontSize: "12px", fontWeight: 700, color: "#231f20", textTransform: "uppercase" }}>
                    Payment Status — Per Payer
                  </p>
                  {allocationsLoading ? (
                    <p style={{ margin: 0, fontSize: "13px", color: "#888" }}>Loading…</p>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12.5px" }}>
                        <thead>
                          <tr style={{ color: "#999", textAlign: "left" }}>
                            <th style={{ padding: "4px 8px", fontWeight: 600 }}>Payer</th>
                            <th style={{ padding: "4px 8px", fontWeight: 600 }}>Share</th>
                            <th style={{ padding: "4px 8px", fontWeight: 600 }}>Amount</th>
                            <th style={{ padding: "4px 8px", fontWeight: 600 }}>Status</th>
                            <th style={{ padding: "4px 8px", fontWeight: 600 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {churchAllocations.map((a) => {
                            const statusMap = {
                              paid: { bg: "#f0fdf4", color: "#16a34a", text: a.payerType === "Student" && a.fulfilled ? "Paid · Enrolled" : "Paid" },
                              awaiting_payment: { bg: "#fff8e1", color: "#d97706", text: "Awaiting Payment" },
                              payment_failed: { bg: "#fef2f2", color: "#dc2626", text: "Failed" },
                              covered: { bg: "#eef1ff", color: "#3a45c9", text: a.payerType === "CEA" ? "CEA Subsidy" : "Covered" },
                            };
                            const badge = statusMap[a.paymentStatus] || { bg: "#f3f4f6", color: "#666", text: a.paymentStatus };
                            return (
                              <tr key={a.id} style={{ borderTop: "1px solid #f0f0f0" }}>
                                <td style={{ padding: "6px 8px", color: "#222" }}>
                                  <strong>{a.payerType}</strong>
                                  {a.payerName && a.payerType === "Student" ? ` — ${a.payerName}` : ""}
                                  {a.payerEmail ? <div style={{ color: "#999", fontSize: "11.5px" }}>{a.payerEmail}</div> : null}
                                </td>
                                <td style={{ padding: "6px 8px", color: "#444" }}>{a.percent}%</td>
                                <td style={{ padding: "6px 8px", color: "#444", whiteSpace: "nowrap" }}>{formatRupiah(a.amount)}</td>
                                <td style={{ padding: "6px 8px" }}>
                                  <span style={{ backgroundColor: badge.bg, color: badge.color, fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "8px", whiteSpace: "nowrap" }}>
                                    {badge.text}
                                  </span>
                                </td>
                                <td style={{ padding: "6px 8px", textAlign: "right" }}>
                                  {a.paymentStatus === "payment_failed" && a.payerEmail && (
                                    <button
                                      type="button"
                                      className="adm-btn adm-btn--primary adm-btn--sm"
                                      style={{ whiteSpace: "nowrap" }}
                                      disabled={resendingAllocId !== null}
                                      onClick={() => handleResendAllocation(a)}
                                      title="Send a new payment link to this payer's email"
                                    >
                                      {resendingAllocId === a.id ? "Sending..." : "Resend Link"}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {(typeof churchDetails.students === "string"
                ? JSON.parse(churchDetails.students)
                : churchDetails.students
              ).map((s, i) => (
                <div
                  key={i}
                  style={{
                    border: "1px solid #e5e5e5",
                    borderRadius: "8px",
                    marginBottom: "14px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      backgroundColor: "#231f20",
                      color: "#fff",
                      padding: "8px 16px",
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    Student {i + 1} — {s.name}
                  </div>
                  <div style={{ padding: "12px 16px" }}>
                    {editableEmailRow("Email", s.email, () =>
                      handleChurchEmailEdit("student", s.email, s.name),
                    )}
                    {detailRow("Whatsapp", s.phoneNumber)}
                    {detailRow("Date of Birth", formatDmy(s.dateOfBirth))}
                    {detailRow("Address", s.address)}
                    {detailRow("City", `${s.city}${s.province ? `, ${s.province}` : ""} ${s.postalCode || ""}`)}
                    {detailRow("Student Package", s.studentPackage)}
                    {detailRow("Role", s.role)}
                    {detailRow("Class Type", s.classType)}
                    {detailRow("Program", s.program)}
                    {s.program === "1 Year Program" && detailRow("Starting Class", s.startingClass)}
                    {s.program === "1 Year Program" && detailRow("Tshirt Size", (s.tshirtSize || "").toUpperCase())}
                    {detailRow(
                      "Classes",
                      (s.classes?.className || []).map((c) => c.name).join(", "),
                    )}
                    {detailRow("Joined CEA Before", s.joinedCEA)}
                    {s.joinedCEA === "Yes" && detailRow("Year Joined", s.yearJoined)}
                    {detailRow("Kisah kekristenan", s.question1)}
                    {detailRow("Baptisan", s.question2)}
                    {detailRow("Pengalaman pelayanan", s.question3)}
                    {detailRow("Rencana selanjutnya", s.question4)}
                    {detailRow("Kisah lahir baru", s.question5)}
                  </div>
                </div>
              ))}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "18px" }}>
                <button
                  type="button"
                  className="adm-btn adm-btn--danger"
                  onClick={() => handleRejectChurch(churchDetails)}
                >
                  {churchDetails.paymentStatus === "awaiting_payment" ? "Cancel Registration" : "Reject"}
                </button>
                {churchDetails.paymentStatus !== "awaiting_payment" && churchAllocations.length === 0 && (
                  <button
                    type="button"
                    className="adm-btn adm-btn--primary"
                    onClick={() => handleApproveChurch(churchDetails)}
                  >
                    Approve All
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* ══════════ PAYMENT ALLOCATION — FULL-SCREEN OVERLAY ══════════ */}
      {allocationReg &&
        createPortal(
          <div
            className="adm-modal-backdrop"
            style={{ zIndex: 2500, overflowY: "auto", alignItems: "flex-start", padding: "24px 16px" }}
          >
            <div
              className="adm-modal"
              style={{ width: "1080px", maxHeight: "none", overflow: "visible", backgroundColor: "var(--adm-bg)", padding: "22px" }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "18px" }}>
                <div>
                  <h5 className="adm-card-title" style={{ fontSize: "18px" }}>Payment Allocation</h5>
                  <p className="adm-card-subtitle">
                    Choose the discount (CEA share) and split the remaining payment across payers.
                  </p>
                </div>
                <button
                  type="button"
                  className="adm-modal-close"
                  aria-label="Close"
                  onClick={() => setAllocationReg(null)}
                  disabled={allocSubmitting}
                >
                  ×
                </button>
              </div>
              <PaymentAllocationSimulator
                embedded
                registration={allocationReg}
                initialDiscount={allocationReg.discountPercent || 0}
                onConfirm={handleAllocationConfirm}
                onClose={() => setAllocationReg(null)}
                submitting={allocSubmitting}
              />
            </div>
          </div>,
          document.body,
        )}

      {/* ══════════ REGULAR STUDENT — SEE DETAILS MODAL ══════════ */}
      {studentDetails &&
        createPortal(
          <div className="adm-modal-backdrop" onClick={() => setStudentDetails(null)}>
            <div
              className="adm-modal"
              onClick={(e) => e.stopPropagation()}
              style={{ width: "560px", padding: "24px" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "16px",
                }}
              >
                <div>
                  <h5 style={{ margin: 0, fontWeight: 700 }}>{studentDetails.name}</h5>
                  <p style={{ margin: "2px 0 0", fontSize: "13px", color: "#888" }}>
                    {studentDetails.studentId}
                  </p>
                </div>
                <button
                  onClick={() => setStudentDetails(null)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "20px",
                    lineHeight: 1,
                    cursor: "pointer",
                    color: "#888",
                  }}
                >
                  ×
                </button>
              </div>

              {detailRow("Email", studentDetails.email)}
              {detailRow("Whatsapp", studentDetails.phoneNumber)}
              {detailRow("Date of Birth", formatDmy(studentDetails.dateOfBirth))}
              {detailRow("Address", studentDetails.address)}
              {detailRow(
                "City",
                `${studentDetails.city || ""}${studentDetails.province ? `, ${studentDetails.province}` : ""} ${studentDetails.postalCode || ""}`,
              )}
              {detailRow("Student Package", studentDetails.studentPackage)}
              {detailRow("Local Church", studentDetails.localChurch)}
              {detailRow("Last DJ Class", studentDetails.lastDj)}
              {detailRow("Role", studentDetails.role)}
              {detailRow("Class Type", studentDetails.classType)}
              {detailRow("Program", studentDetails.program)}
              {studentDetails.program === "1 Year Program" &&
                detailRow("Tshirt Size", (studentDetails.tshirtSize || "").toUpperCase())}
              {detailRow(
                "Classes",
                (
                  (typeof studentDetails.classList === "string"
                    ? JSON.parse(studentDetails.classList)
                    : studentDetails.classList
                  )?.className || []
                )
                  .filter((c) => c.status === "requested")
                  .map((c) => c.name)
                  .join(", "),
              )}
              {detailRow("Joined CEA Before", studentDetails.joinedCEA)}
              {studentDetails.joinedCEA === "Yes" && detailRow("Year Joined", studentDetails.yearJoined)}
              {detailRow("Kisah kekristenan", studentDetails.question1)}
              {detailRow("Baptisan", studentDetails.question2)}
              {detailRow("Pengalaman pelayanan", studentDetails.question3)}
              {detailRow("Rencana selanjutnya", studentDetails.question4)}
              {detailRow("Kisah lahir baru", studentDetails.question5)}

              <div
                style={{
                  backgroundColor: "#fff8f7",
                  border: "1px solid #fbd5cc",
                  borderRadius: "6px",
                  padding: "14px 16px",
                  marginTop: "14px",
                }}
              >
                <p style={{ margin: "0 0 8px", fontSize: "12px", fontWeight: 700, color: "#fb3104", textTransform: "uppercase" }}>
                  Payment
                </p>
                {detailRow("Discount", studentDetails.discountPercent ? `${studentDetails.discountPercent}%` : "")}
                {detailRow("Final Price", formatRupiah(studentDetails.finalPrice))}
                {detailRow("Payment Status", studentDetails.paymentStatus)}
                {detailRow("Payment Plan", studentDetails.paymentPlan === "installment" ? "Installment" : "Full Payment")}
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "18px" }}>
                <button
                  type="button"
                  className="adm-btn adm-btn--danger"
                  onClick={() => {
                    const email = studentDetails.email;
                    const status = studentDetails.paymentStatus;
                    setStudentDetails(null);
                    handleReject(email, status);
                  }}
                >
                  {studentDetails.paymentStatus === "awaiting_payment" ? "Cancel Registration" : "Reject"}
                </button>
                {studentDetails.paymentStatus === "awaiting_payment" && (
                  <button
                    type="button"
                    className="adm-btn adm-btn--primary"
                    onClick={() => {
                      const item = studentDetails;
                      setStudentDetails(null);
                      handleResendAwaiting(item);
                    }}
                  >
                    Resend Payment Link
                  </button>
                )}
                {studentDetails.paymentStatus !== "awaiting_payment" && (
                  <button
                    type="button"
                    className="adm-btn adm-btn--primary"
                    onClick={() => {
                      const email = studentDetails.email;
                      const discount = studentDetails.discountPercent;
                      const status = studentDetails.paymentStatus;
                      setStudentDetails(null);
                      handleApprove(email, discount, status);
                    }}
                  >
                    {studentDetails.paymentStatus === "payment_failed" ? "Resend Payment Link" : "Approve"}
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ApprovalPage;
